import { translateFn } from 'apps/webcomponents/src/utils/translator.helper';
import { LitElement, html, PropertyValues } from 'lit';
import { query, customElement, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { getAssetTypeValues } from '../types';
import { InputWidthValues } from 'apps/webcomponents/src/ui/gzInput';
import { EASMScanConfigurationEvents } from '../../easmScanConfiguration/types';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { Subscription } from 'rxjs';
import { GzButtonWebComponent } from '../../../../ui/gzButton';
import { GzInputErrorWebComponent } from '../../../../ui/gzForm/inputError';
import { FormValidator } from '../../../../ui/gzForm/formField/types/form-validator';
import { RequiredValidator } from '../../../../ui/gzForm/validators/required.validator';
import { IpValidator } from '../../../../ui/gzForm/validators/ip.validator';
import { EmailValidator } from '../../../../ui/gzForm/validators/email.validator';
import { NumericValidator } from '../../../../ui/gzForm/validators/numeric.validator';
import { DomainValidator } from '../../../../ui/gzForm/validators/domain.validator';
import { RegexValidator } from '../../../../ui/gzForm/validators/regex.validator';
import { AssetType } from '../../easmScanConfigurationAssetGrid/types';
import { debounce } from '../../../../utils/event.utils';

/**
 * @tag gz-easm-scan-configuration-item-form
 */
@customElement('gz-easm-scan-configuration-item-form')
export class EasmScanConfigurationItemFormWebComponent extends LitElement {
  static get styles() {
    return styles;
  }

  @query('#scan-list-form')
  public scanListForm!: HTMLDivElement;

  @query('#asset-type-dropdown-scan-list')
  private assetTypeDropdownScanList!: GzFormFieldWebComponent;

  @query('#asset-input-text-scan-list')
  private assetInputTextScanList!: GzFormFieldWebComponent;

  @query('#add-button-scan-list')
  private addButtonScanList!: GzButtonWebComponent;

  @query('#scan-exclusions-form')
  public scanExclusionsForm!: HTMLDivElement;

  @query('#asset-type-dropdown-scan-exclusions')
  private assetTypeDropdownScanExclusions!: GzFormFieldWebComponent;

  @query('#asset-input-text-scan-exclusions')
  private assetInputTextScanExclusions!: GzFormFieldWebComponent;

  @query('#add-button-scan-exclusions')
  private addButtonScanExclusions!: GzButtonWebComponent;

  @query('#add-assets-error')
  public inputError!: GzInputErrorWebComponent;

  @state()
  private scanListFormGroup: FormGroup | null;

  @state()
  private scanExclusionsFormGroup: FormGroup | null;

  @state()
  private scanListSubscription: Subscription | null;

  @state()
  private scanExclusionsSubscription: Subscription | null;

  render() {
    return html`
      <div class="scan-configuration-asset-form-wrapper">
        <div class="scan-configuration-asset-form">
          <div id="scan-list-form" class="asset-form">
            <gz-text fontSize="14" class="scan-configuration-asset-form--title"
              >${translateFn('Add assets to next scan')}</gz-text
            >
            <div class="scan-configuration-asset-form--row d-flex flex-row align-items-center">
              <div class="scan-configuration-asset-form--field">
                <gz-form-field id="asset-type-dropdown-scan-list" disableVerticalMargin>
                  <gz-dropdown
                    gz-form-input
                    label="${translateFn('Asset type')}"
                    placeholder="${translateFn('Select asset type')}"
                    options="${JSON.stringify(getAssetTypeValues())}"
                    @selectionChanged="${this.handleAssetTypeChangeScanList}"
                  ></gz-dropdown>
                </gz-form-field>
              </div>

              <div class="scan-configuration-asset-form--field excluded-item-input">
                <gz-form-field id="asset-input-text-scan-list" disableVerticalMargin>
                  <gz-input
                    gz-form-input
                    inputWidth=${InputWidthValues.XLARGE}
                    placeholder="${translateFn('Type here')}"
                  ></gz-input>
                </gz-form-field>
              </div>

              <div class="scan-configuration-asset-form--actions">
                <gz-icon-button
                  id="add-button-scan-list"
                  size="32"
                  fontSize="20"
                  buttonType="square"
                  icon="ds-icon-add"
                  buttonFill="primary"
                  @click="${debounce(event => this.addAssetsToNextScan(), 500)}"
                >
                </gz-icon-button>
              </div>
            </div>
          </div>
          <div id="scan-exclusions-form" class="asset-form" hidden>
            <gz-text fontSize="14" class="scan-configuration-asset-form--title"
              >${translateFn('Add assets to exclusion list')}</gz-text
            >
            <div class="scan-configuration-asset-form--row d-flex flex-row align-items-center">
              <div class="scan-configuration-asset-form--field">
                <gz-form-field id="asset-type-dropdown-scan-exclusions" disableVerticalMargin>
                  <gz-dropdown
                    gz-form-input
                    label="${translateFn('Asset type')}"
                    placeholder="${translateFn('Select asset type')}"
                    options="${JSON.stringify(getAssetTypeValues())}"
                    @selectionChanged="${this.handleAssetTypeChangeScanExclusions}"
                  ></gz-dropdown>
                </gz-form-field>
              </div>

              <div class="scan-configuration-asset-form--field excluded-item-input">
                <gz-form-field id="asset-input-text-scan-exclusions" disableVerticalMargin>
                  <gz-input
                    gz-form-input
                    inputWidth=${InputWidthValues.XLARGE}
                    placeholder="${translateFn('Type here')}"
                  ></gz-input>
                </gz-form-field>
              </div>

              <div class="scan-configuration-asset-form--actions">
                <gz-icon-button
                  id="add-button-scan-exclusions"
                  size="32"
                  fontSize="20"
                  buttonType="square"
                  icon="ds-icon-add"
                  buttonFill="primary"
                  @click="${debounce(event => this.addAssetsToExclusions(), 500)}"
                >
                </gz-icon-button>
              </div>
            </div>
          </div>
        </div>
        <gz-input-error id="add-assets-error"></gz-input-error>
      </div>
    `;
  }

  public firstUpdated(changedProperties: PropertyValues): void {
    super.firstUpdated(changedProperties);
    this.createFormGroups();
    this.inputError.Error = null;
  }

  private createFormGroups(): void {
    [
      this.assetTypeDropdownScanList,
      this.assetInputTextScanList,
      this.assetTypeDropdownScanExclusions,
      this.assetInputTextScanExclusions,
    ].forEach(field => (field.Validators = [RequiredValidator(translateFn('Required field'))]));

    this.scanListFormGroup = new FormGroup({
      assetType: this.assetTypeDropdownScanList,
      assetName: this.assetInputTextScanList,
    });

    this.scanExclusionsFormGroup = new FormGroup({
      assetType: this.assetTypeDropdownScanExclusions,
      assetName: this.assetInputTextScanExclusions,
    });

    this.scanListSubscription = this.scanListFormGroup.valid$.subscribe({
      next: valid => {
        this.addButtonScanList.disabled = !valid;
      },
    });

    this.scanExclusionsSubscription = this.scanExclusionsFormGroup.valid$.subscribe({
      next: valid => {
        this.addButtonScanExclusions.disabled = !valid;
      },
    });
  }

  private addValidatorsForAssetName(isScanList: boolean): Array<FormValidator> {
    let dropdownValue = this.assetTypeDropdownScanExclusions.value;
    const validators = [RequiredValidator(translateFn('Required field'))];

    if (isScanList) {
      validators.push(RegexValidator(/[^\?\*\[\]\!]+/, translateFn('You cannot use wildcards')));
      dropdownValue = this.assetTypeDropdownScanList.value;
    }

    switch (dropdownValue) {
      case AssetType.IPv4:
        validators.push(IpValidator(translateFn('Invalid IPv4 address.'), false, false));
        break;
      case AssetType.IPv6:
        validators.push(IpValidator(translateFn('Invalid IPv6 address.'), true, false));
        break;
      case AssetType.EMAIL:
        if (isScanList) {
          validators.push(EmailValidator(translateFn('Enter a valid email address.')));
        } else {
          validators.push(EmailValidator(translateFn('Enter a valid email address.'), true));
        }
        break;
      case AssetType.DOMAIN:
        if (isScanList) {
          validators.push(DomainValidator(translateFn('Invalid domain name.')));
        } else {
          validators.push(DomainValidator(translateFn('Invalid domain name.'), true));
        }
        break;
      default:
        break;
    }

    return validators;
  }

  private handleAssetTypeChangeScanList(): void {
    this.assetInputTextScanList.Validators = this.addValidatorsForAssetName(true);
  }

  private handleAssetTypeChangeScanExclusions(): void {
    this.assetInputTextScanExclusions.Validators = this.addValidatorsForAssetName(false);
  }

  private addAssetsToNextScan(): void {
    this.dispatchEvent(
      new CustomEvent(EASMScanConfigurationEvents.ADD_ASSET_TO_SCAN, {
        detail: {
          assetType: this.assetTypeDropdownScanList.value,
          assetName: this.assetInputTextScanList.value,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  private addAssetsToExclusions(): void {
    this.dispatchEvent(
      new CustomEvent(EASMScanConfigurationEvents.ADD_ASSET_TO_SCAN, {
        detail: {
          assetType: this.assetTypeDropdownScanExclusions.value,
          assetName: this.assetInputTextScanExclusions.value,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  public disconnectedCallback(): void {
    this.scanListFormGroup?.unsubscribe();
    this.scanListSubscription?.unsubscribe();
    this.scanExclusionsFormGroup?.unsubscribe();
    this.scanExclusionsSubscription?.unsubscribe();

    super.disconnectedCallback();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'easm-scan-configuration-item-form': EasmScanConfigurationItemFormWebComponent;
  }
}
